import React, { useState } from 'react';
import styled from 'styled-components';
import { FormField, Button, ButtonGroup, Row } from './FormElements';
import axios from 'axios';

const FormContainer = styled.div`
  width: 100%;
`;

const Title = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.colors.text};
`;

const SectionTitle = styled.h4`
  font-size: 1rem;
  margin: 1.5rem 0 1rem;
  color: ${props => props.theme.colors.text};
  font-weight: 500;
`;

const Description = styled.p`
  font-size: 0.95rem;
  color: ${props => props.theme.colors.lightText};
  line-height: 1.6;
  margin: 0 0 1.75rem;
`;

const LocationPaymentScreen = ({ formData, updateFormData, prevStep, nextStep }) => {
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validateCEP = (cep) => {
    const cepRegex = /^\d{5}-\d{3}$/;
    return cepRegex.test(cep);
  };

  const searchCEP = async (cep) => {
    if (!validateCEP(cep)) return;
    
    try {
      setLoading(true);
      const cleanCep = cep.replace(/[^\d]/g, '');
      const response = await axios.get(`https://viacep.com.br/ws/${cleanCep}/json/`);
      
      if (response.data.erro) {
        setErrors({
          ...errors,
          cep: 'CEP não encontrado'
        });
        return;
      }
      
      updateFormData({
        rua: response.data.logradouro,
        cidade: response.data.localidade,
        estado: response.data.uf
      });
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      setErrors({
        ...errors,
        cep: 'Erro ao buscar CEP'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCEPBlur = (e) => {
    const cep = e.target.value;
    if (cep && validateCEP(cep)) {
      searchCEP(cep);
    }
  };

  const brStates = [
    { value: 'AC', label: 'Acre' },
    { value: 'AL', label: 'Alagoas' },
    { value: 'AP', label: 'Amapá' },
    { value: 'AM', label: 'Amazonas' },
    { value: 'BA', label: 'Bahia' },
    { value: 'CE', label: 'Ceará' },
    { value: 'DF', label: 'Distrito Federal' },
    { value: 'ES', label: 'Espírito Santo' },
    { value: 'GO', label: 'Goiás' },
    { value: 'MA', label: 'Maranhão' },
    { value: 'MT', label: 'Mato Grosso' },
    { value: 'MS', label: 'Mato Grosso do Sul' },
    { value: 'MG', label: 'Minas Gerais' },
    { value: 'PA', label: 'Pará' },
    { value: 'PB', label: 'Paraíba' },
    { value: 'PR', label: 'Paraná' },
    { value: 'PE', label: 'Pernambuco' },
    { value: 'PI', label: 'Piauí' },
    { value: 'RJ', label: 'Rio de Janeiro' },
    { value: 'RN', label: 'Rio Grande do Norte' },
    { value: 'RS', label: 'Rio Grande do Sul' },
    { value: 'RO', label: 'Rondônia' },
    { value: 'RR', label: 'Roraima' },
    { value: 'SC', label: 'Santa Catarina' },
    { value: 'SP', label: 'São Paulo' },
    { value: 'SE', label: 'Sergipe' },
    { value: 'TO', label: 'Tocantins' }
  ];

  // Removed unused variable to satisfy ESLint
  // const paymentMethods = [
  //   { value: 'BOLETO', label: 'Boleto' },
  //   { value: 'CREDIT_CARD', label: 'Cartão de Crédito' },
  //   { value: 'PIX', label: 'Pix' },
  //   { value: 'BANK_TRANSFER', label: 'Transferência Bancária' }
  // ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    updateFormData({ [name]: value });
    
    // Clear error when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    const newErrors = {};
    
    if (!formData.cep) {
      newErrors.cep = 'O CEP é obrigatório';
    } else if (!validateCEP(formData.cep)) {
      newErrors.cep = 'Digite um CEP válido no formato XXXXX-XXX';
    }
    
    if (!formData.rua) {
      newErrors.rua = 'A rua é obrigatória';
    } else if (formData.rua.length < 3 || formData.rua.length > 120) {
      newErrors.rua = 'Use entre 3 e 120 caracteres';
    }

    if (!formData.numero) {
      newErrors.numero = 'O número é obrigatório';
    } else if (isNaN(formData.numero) || parseInt(formData.numero, 10) <= 0) {
      newErrors.numero = 'Digite um número válido';
    }

    if (!formData.cidade) {
      newErrors.cidade = 'A cidade é obrigatória';
    } else if (formData.cidade.length < 3 || formData.cidade.length > 100) {
      newErrors.cidade = 'Use entre 3 e 100 caracteres';
    }

    if (!formData.estado) {
      newErrors.estado = 'O estado é obrigatório';
    }
    

    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Submit form
    nextStep();
  };

  return (
    <FormContainer>
      <Title>Localização</Title>
      <Description>
        Informe o endereço completo da empresa. Usaremos esses dados para direcionar o contato regional.
      </Description>
      <form onSubmit={handleSubmit}>
        <SectionTitle>Endereço</SectionTitle>
        <FormField
          label="CEP"
          name="cep"
          placeholder="Digite o CEP"
          value={formData.cep}
          onChange={handleChange}
          onBlur={handleCEPBlur}
          error={errors.cep}
          mask="99999-999"
          disabled={loading}
        />

        <FormField
          label="Rua"
          name="rua"
          placeholder="Digite a rua da empresa"
          value={formData.rua}
          onChange={handleChange}
          error={errors.rua}
        />

        <Row>
          <FormField
            label="Número"
            name="numero"
            type="number"
            placeholder="Digite o número"
            value={formData.numero}
            onChange={handleChange}
            error={errors.numero}
          />
          <FormField
            label="Complemento"
            name="complemento"
            placeholder="Opcional"
            value={formData.complemento}
            onChange={handleChange}
            error={errors.complemento}
          />
        </Row>

        <Row>
          <FormField
            label="Cidade"
            name="cidade"
            placeholder="Digite a cidade da empresa"
            value={formData.cidade}
            onChange={handleChange}
            error={errors.cidade}
          />
          <FormField
            label="Estado"
            name="estado"
            type="select"
            placeholder="Escolha a UF da empresa"
            value={formData.estado}
            onChange={handleChange}
            error={errors.estado}
            options={brStates}
          />
        </Row>
        
        <ButtonGroup>
          <Button type="button" onClick={prevStep}>
            Voltar
          </Button>
          <Button type="submit" primary>
            Avançar
          </Button>
        </ButtonGroup>
      </form>
    </FormContainer>
  );
};

export default LocationPaymentScreen;
